import { useQuery, useMutation } from "@tanstack/react-query";
import { useVenue } from "../lib/venue";
import { useAuth } from "../lib/auth";
import { getAdminOverview, submitVenueForReview } from "../lib/api";
import { pushToast } from "./toast";

// Onboarding draft gate. A venue with appLive=false is a PRIVATE draft:
//  - the public (anyone who isn't an admin/owner) sees a "coming soon" screen
//   , the app is not live and nobody can subscribe;
//  - the owner/admin sees the full app so they can design it, plus a banner to
//    submit it to Assembly for review.
// Live venues (the default) render straight through.

function useIsVenueAdmin(venueId: string | undefined, enabled: boolean) {
  const { user, isLoading: authLoading } = useAuth();
  const q = useQuery({
    queryKey: ["admin-overview", venueId],
    queryFn: () => getAdminOverview(venueId!),
    enabled: enabled && !!user && !!venueId,
    retry: false,
  });
  return {
    authLoading,
    user,
    // resolving while we still don't know
    resolving: authLoading || (!!user && enabled && q.isLoading),
    isAdmin: !!user && q.isSuccess,
  };
}

export function DraftGate({ children }: { children: React.ReactNode }) {
  const { data } = useVenue();
  const venue = data?.venue;
  const isDraft = venue?.appLive === false;
  const { resolving, isAdmin } = useIsVenueAdmin(venue?.id, isDraft);

  if (!isDraft) return <>{children}</>;
  if (resolving) {
    return <div style={{ minHeight: "100dvh", background: "var(--bg, #0a0a0b)" }} aria-hidden />;
  }
  if (!isAdmin) return <ComingSoon name={venue?.appName ?? venue?.name ?? "This venue"} />;
  return <>{children}</>;
}

function ComingSoon({ name }: { name: string }) {
  return (
    <div
      style={{
        minHeight: "100dvh",
        display: "grid",
        placeItems: "center",
        padding: 24,
        background: "var(--bg, #0a0a0b)",
        color: "var(--fg, #ededed)",
        fontFamily: "var(--venue-font, ui-sans-serif, system-ui, sans-serif)",
        textAlign: "center",
      }}
    >
      <div style={{ maxWidth: 420 }}>
        <div
          style={{
            display: "inline-block",
            padding: "4px 12px",
            borderRadius: 999,
            background: "var(--accent, #6366f1)",
            color: "var(--accent-fg, #fff)",
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
          }}
        >
          Coming soon
        </div>
        <h1 style={{ fontSize: 28, fontWeight: 800, margin: "18px 0 0", letterSpacing: "-0.02em" }}>
          {name} is on its way
        </h1>
        <p style={{ marginTop: 12, opacity: 0.7, lineHeight: 1.6, fontSize: 15 }}>
          We&apos;re putting the finishing touches on the app. Check back soon.
        </p>
      </div>
    </div>
  );
}

// Owner-only banner shown over a draft venue. Renders nothing for live venues.
export function DraftBanner() {
  const { data } = useVenue();
  const venue = data?.venue;
  if (!venue || venue.appLive !== false) return null;
  return <DraftBannerInner venueId={venue.id} submitted={!!venue.appReviewRequestedAt} />;
}

function DraftBannerInner({ venueId, submitted }: { venueId: string; submitted: boolean }) {
  const submit = useMutation({
    mutationFn: () => submitVenueForReview(venueId),
    onSuccess: () => pushToast("Sent to Assembly for review, we'll be in touch.", "success"),
    meta: { silentError: false },
  });
  return (
    <div
      style={{
        background: "rgba(245, 158, 11, 0.12)",
        borderBottom: "1px solid rgba(245, 158, 11, 0.4)",
        color: "var(--fg, #ededed)",
        padding: "10px 16px",
        display: "flex",
        alignItems: "center",
        gap: 12,
        flexWrap: "wrap",
        fontSize: 13,
      }}
    >
      <span style={{ fontWeight: 700, color: "#f59e0b" }}>Draft</span>
      <span style={{ opacity: 0.85, flex: 1, minWidth: 180 }}>
        Only you can see this. {submitted ? "Pending Assembly review." : "Design it, then submit for review to go live."}
      </span>
      {!submitted && (
        <button
          onClick={() => submit.mutate()}
          disabled={submit.isPending}
          style={{
            background: "#f59e0b",
            color: "#1a1205",
            border: "none",
            borderRadius: 8,
            padding: "6px 14px",
            fontWeight: 700,
            fontSize: 13,
            cursor: "pointer",
            opacity: submit.isPending ? 0.6 : 1,
          }}
        >
          {submit.isPending ? "Submitting…" : "Submit for review"}
        </button>
      )}
      {submitted && (
        <span style={{ fontWeight: 700, color: "#f59e0b" }}>✓ Submitted</span>
      )}
    </div>
  );
}
